import { Control } from "../Control.js";
import { resolveValue } from "../utils/ui-helpers.js";

class MenuNode {

    constructor(name, label, url, nodes, isGroup, isExpanded) {

        this.name = name;
        this.label = label;
        this.url = url;
        this.nodes = nodes || [];

        this.isExpanded = isExpanded;
        this.isGroup = isGroup;
        this.isVisible = true;
        this.isActive = false;
        this.parent = null;

    };

    hasSubnodes() {
        return ((this.isGroup && this.nodes && this.nodes.length) || 0) > 0;
    };

    toggleState() {
        this.isExpanded = !this.isExpanded;
    };

    toggleStateAndSubnodes(state) {
        this.isExpanded = (state === undefined ? !this.isExpanded : state);
        var newState = this.isExpanded;
        (this.nodes || []).forEach(function (item) {
            item.toggleStateAndSubnodes(newState);
        })
    };

}

// --------------------------------------------------------------------------

export class Menu extends Control {

    constructor(parent, options = {}) {

        options = Object.assign({
            items: [],
            active: false,
        }, options);

        super(parent, options);

        const {
            items = [],
            active = false,
        } = options;

        this.items = items;
        this.active = active;

    };

    getItems() { return resolveValue(this.items); };

    isActive() { return resolveValue(this.active); }

    async prepare(params) {

        this.ensureNodes();
        this.updateNodesState();
        return true;

    };

    async refresh() {

        this.ensureNodes();
        this.updateNodesState();
        return true;

    };

    update() {

        this.ensureNodes();
        this.updateNodesState();
        this.invalidate();

    };

    show() {

        this.active = true;
        this.invalidate();

    }

    hide() {

        this.active = false;
        this.invalidate();

    }

    ensureNodes() {

        var that = this,
            app = this.getApp(),
            nodesByName = {},
            items = this.getItems(),
            adminMenu;

        if (this.root)
            return this.root;

        this.root = new MenuNode('$root', '$root', null, [], true, false);
        nodesByName.$root = this.root;
        createSubonodes(this.root, items);
        return this.root;

        function createSubonodes(root, defs, autocreateParent) {

            (defs || []).forEach(function (def) {
                validateMenuDef(def);

                var routes = (def.route && [def.route]) || def.routes || [],
                    route = (routes && routes[0]) || null,
                    path = def.path,
                    url = (route && app.getRouteUrl(route)) || (path && app.getModuleUrl(path)) || null;

                let node = getOrCreateNode(def, url);
                node.def = def;
                node.routes = routes;
                node.icon = def.icon ?? node.icon ?? null;

                if (def.parent) {
                    let parentNode = nodesByName[def.parent];
                    if (!parentNode)
                        throw new Error(`Parent node "${def.parent}" not found!`);
                    attachNode(parentNode, node);
                }
                else {
                    attachNode(root, node);
                }

            });

            return root;

        };

        function getOrCreateNode(def, url) {

            if (def.name && nodesByName[def.name]) {
                let existingNode = nodesByName[def.name];
                existingNode.label = def.label ?? existingNode.label;
                existingNode.url = url ?? existingNode.url;
                existingNode.isGroup = existingNode.isGroup || def.type == 'group';
                return existingNode;
            }

            let node = new MenuNode(def.name, def.label, url, [], def.type == 'group', false);
            node.routes = [];
            node.icon = null;

            if (def.name)
                nodesByName[def.name] = node;

            return node;

        }

        function attachNode(parentNode, node) {

            if (!parentNode || !node)
                return;

            if (node.parent === parentNode) {
                if (!parentNode.nodes.includes(node))
                    parentNode.nodes.push(node);
                return;
            }

            if (node.parent) {
                let previousParent = node.parent;
                let previousIndex = previousParent.nodes.indexOf(node);
                if (previousIndex >= 0)
                    previousParent.nodes.splice(previousIndex, 1);
            }

            node.parent = parentNode;

            if (!parentNode.nodes.includes(node))
                parentNode.nodes.push(node);

        }

        function validateMenuDef(def) {

            if (!def || typeof def !== 'object')
                throw new Error('[Menu] Menu definition must be an object.');
            if (!def.name)
                throw new Error('[Menu] Menu definition "name" is required.');
            if (!def.label)
                throw new Error(`[Menu] Menu definition "${def.name}" is missing "label".`);
            if (!def.type)
                throw new Error(`[Menu] Menu definition "${def.name}" is missing "type".`);
            if (def.type !== 'item' && def.type !== 'group')
                throw new Error(`[Menu] Menu definition "${def.name}" has unsupported type "${def.type}".`);

        }

    };

    updateNodesState() {

        if (!this.root)
            return;

        const that = this;
        const authManager = this.getAuthManager();
        const activeRouteName = this.getApp()?.getActiveRouteName?.() ?? null;

        walk(this.root, (node) => {
            node.isActive = false;
        }, true);

        updateVisibility(this.root);

        this.activateNodeByRoute(activeRouteName, false);

        function walk(node, handler, includeRoot = false) {
            if (!node)
                return;

            if (includeRoot || node !== that.root)
                handler(node);
            (node.nodes || []).forEach((childNode) => {
                walk(childNode, handler, includeRoot);
            });
        }

        function updateVisibility(node) {

            if (!node)
                return false;

            if (node === that.root) {
                const hasVisibleChildren = collectVisibleChildren(node.nodes || []);
                node.isVisible = hasVisibleChildren;
                return node.isVisible;
            }

            const def = node.def ?? null;
            const hasSubnodes = !!(node.nodes && node.nodes.length);
            const isDirectlyVisible = isNodeDirectlyVisible(def, hasSubnodes);
            const hasVisibleChildren = hasSubnodes
                ? collectVisibleChildren(node.nodes || [])
                : false;

            node.isVisible = hasSubnodes
                ? (isDirectlyVisible && hasVisibleChildren)
                : isDirectlyVisible;

            return node.isVisible;

        }

        function isNodeDirectlyVisible(def, isGroupNode) {

            if (def?.public === true)
                return true;

            if (isGroupNode && !Object.hasOwn(def || {}, 'privileges'))
                return true;

            return authManager.validateUserPrivileges(def?.privileges);

        }

        function collectVisibleChildren(childNodes) {

            let hasVisibleChildren = false;

            (childNodes || []).forEach((childNode) => {
                if (updateVisibility(childNode))
                    hasVisibleChildren = true;
            });

            return hasVisibleChildren;

        }

    };

    getFirstVisibleNode() {

        return firstVisibleNode(this.root.nodes);

        function firstVisibleNode(items) {

            return (items || []).reduce(function (result, item) {
                if (result)
                    return result;
                if (!item.isVisible)
                    return null;
                if (item.isGroup)
                    return firstVisibleNode(item.nodes);
                return item;
            }, null);

        };


    };

    activateNodeByRoute(routeName, invalidate = true) {

        this.activateNodeByCondition(this.root, (node) => {
            return node.isVisible && (node.routes && node.routes.includes(routeName));
        }, 0);
        if (invalidate)
            this.invalidate();

    };

    activateNodeByCondition(node, condition, level) {

        var that = this;

        node.isExpanded = (node.nodes || []).reduce(function (result, n) {
            // neotacet, nebo nebude volano pro vsechny prvky kvuli kompilacni optimalizaci
            //return that.activateNodeByCondition(n, condition, level + 1) || n.isExpanded || result;
            return that.activateNodeByCondition(n, condition, level + 1) || result;
        }, false) || node.isExpanded;
        node.isActive = condition(node);
        return node.isActive || node.isExpanded;

    };

    _render(r, vm) {

        const renderer = r;
        const menu = this;

        return renderList(this.root.nodes);

        function renderList(items, active = false) {

            return renderer.menu({ css: active ? 'active' : null }, items.map(item => renderItem(item)));

        };

        function renderItem(item) {

            if (!item.isVisible)
                return null;

            const hasSubnodes = item.hasSubnodes();
            const itemOptions = {
                css: item.isActive ? 'active' : null,
                url: item.isGroup ? null : item.url,
                props: {},
                onClick: hasSubnodes
                    ? (e) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        item.toggleState();
                        menu.invalidate();
                    }
                    : null,
            };
            const label = [
                renderer.el('span', 'max', null, [item.label]),
                (hasSubnodes
                    ? renderer.icon('#expand', { rotate: item.isExpanded ? 180 : null })
                    : null
                )
            ].filter((node) => node !== null && node !== undefined);
            const subMenu = hasSubnodes
                ? renderList(item.nodes, item.isExpanded)
                : null;

            return renderer.menuItem(label, item.icon ?? null, itemOptions, subMenu);

        };

    };

}
